import type { ReactNode } from "react";
import { AlertTriangle, RefreshCw, WifiOff } from "lucide-react";
import { ApiError, NetworkError } from "@baze/api-client";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";
import { errorMessage } from "@/lib/api";
import { t } from "@/i18n/id";
import { BrandShapes } from "./BrandShapes";

export function EmptyState({
  title,
  body,
  action,
  illustration,
  className,
  compact,
}: {
  title: ReactNode;
  body?: ReactNode;
  action?: ReactNode;
  illustration?: ReactNode;
  className?: string;
  compact?: boolean;
}) {
  return (
    <div className={cn("flex flex-col items-center text-center", compact ? "gap-3 px-4 py-8" : "gap-4 px-6 py-14", className)}>
      {illustration ?? (compact ? null : <BrandShapes className="h-24 w-40" />)}
      <div className="max-w-md space-y-1.5">
        <h2 className={cn("font-bold tracking-tight text-slate-900", compact ? "text-base" : "text-xl")}>{title}</h2>
        {body ? <p className="text-sm leading-relaxed text-slate-500">{body}</p> : null}
      </div>
      {action ? <div className="mt-1 flex flex-wrap justify-center gap-2">{action}</div> : null}
    </div>
  );
}

/** Friendly error with retry. Shows the API's Indonesian message and request id when available. */
export function ErrorState({ error, onRetry, title, className, compact }: { error?: unknown; onRetry?: () => void; title?: string; className?: string; compact?: boolean }) {
  const offline = error instanceof NetworkError;
  const reqId = error instanceof ApiError ? error.requestId : undefined;
  const Icon = offline ? WifiOff : AlertTriangle;
  return (
    <div role="alert" className={cn("flex flex-col items-center gap-3 text-center", compact ? "px-4 py-6" : "px-6 py-14", className)}>
      <span className="grid size-12 place-items-center rounded-2xl bg-warning-soft text-amber-700">
        <Icon className="size-6" aria-hidden="true" />
      </span>
      <div className="max-w-md space-y-1">
        <h2 className="text-base font-bold text-slate-900">{title ?? t.errors.loadFailed}</h2>
        <p className="text-sm text-slate-500">{error ? errorMessage(error) : t.errors.loadFailedBody}</p>
        {reqId ? <p className="text-xs text-slate-400">{t.errors.requestId(reqId)}</p> : null}
      </div>
      {onRetry ? (
        <Button variant="secondary" size="sm" onClick={onRetry} leftIcon={<RefreshCw aria-hidden="true" />}>
          {t.common.retry}
        </Button>
      ) : null}
    </div>
  );
}
